<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import TripLayout from '../../components/layout/TripLayout.svelte';
  import { Heart, Plus, ExternalLink, MapPin, Trash2, Calendar, Link as LinkIcon } from 'lucide-svelte';

  let { trip, scrapedLinks = [], scraped_links = scrapedLinks }: {
    trip: any;
    scrapedLinks?: any[];
    scraped_links?: any[];
  } = $props();

  let showModal = $state(false);
  let urlInput = $state('');
  let isLoading = $state(false);
  let isParsing = $state(false);
  let notification: { type: 'success' | 'error' | 'info'; message: string } | null = $state(null);

  function openAddModal() {
    urlInput = '';
    showModal = true;
  }

  function closeModal() {
    showModal = false;
    urlInput = '';
  }

  async function addLink() {
    if (!urlInput.trim()) {
      showNotification('error', 'URL을 입력해주세요');
      return;
    }

    // URL 형식 검증
    try {
      new URL(urlInput);
    } catch {
      showNotification('error', '올바른 URL 형식이 아닙니다');
      return;
    }

    isLoading = true;
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/scraped_links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ scraped_link: { original_url: urlInput } })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.parse_failed) {
          // 링크는 저장되었지만 파싱에 실패한 경우
          showNotification('info', '링크가 추가되었습니다. 정보 추출은 나중에 다시 시도해주세요.');
        } else {
          showNotification('success', '링크가 추가되었습니다');
        }
        closeModal();
        router.reload();
      } else {
        const data = await response.json();
        const errorMessage = data.error || '추가에 실패했습니다';

        // 구체적인 에러 메시지 제공
        if (errorMessage.includes('duplicate') || errorMessage.includes('이미')) {
          showNotification('error', '이미 추가된 링크입니다');
        } else if (errorMessage.includes('invalid') || errorMessage.includes('유효하지')) {
          showNotification('error', '유효하지 않은 URL입니다');
        } else {
          showNotification('error', errorMessage);
        }
      }
    } catch (e) {
      showNotification('error', '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.');
    } finally {
      isLoading = false;
    }
  }

  // ========== 파싱 에러 처리 강화 ==========
  interface ParseError {
    linkId: string;
    message: string;
    canRetry: boolean;
  }

  let parseErrors: Map<string, ParseError> = $state(new Map());

  async function parseLink(link: any) {
    isParsing = true;
    // 기존 에러 제거
    parseErrors.delete(link.id);
    parseErrors = parseErrors;

    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/scraped_links/${link.id}/parse`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showNotification('success', '링크 정보가 업데이트되었습니다');
          router.reload();
        } else {
          // 파싱은 성공했지만 메타데이터를 가져오지 못한 경우
          handleParseError(link.id, data.message || '메타데이터를 가져올 수 없습니다', true);
        }
      } else {
        // HTTP 에러
        const errorData = await response.json().catch(() => ({}));
        handleParseError(link.id, errorData.error || '서버 오류가 발생했습니다', true);
      }
    } catch (e) {
      // 네트워크 에러
      handleParseError(link.id, '네트워크 오류로 파싱에 실패했습니다', true);
    } finally {
      isParsing = false;
    }
  }

  function handleParseError(linkId: string, message: string, canRetry: boolean) {
    parseErrors.set(linkId, { linkId, message, canRetry });
    parseErrors = parseErrors;
    showNotification('error', message);
  }

  function dismissParseError(linkId: string) {
    parseErrors.delete(linkId);
    parseErrors = parseErrors;
  }

  function getParseError(linkId: string): ParseError | undefined {
    return parseErrors.get(linkId);
  }

  async function toggleWishlist(link: any) {
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/scraped_links/${link.id}/toggle_wishlist`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });

      if (response.ok) {
        router.reload();
      }
    } catch (e) {
      showNotification('error', '업데이트에 실패했습니다');
    }
  }

  async function addToSchedule(link: any) {
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/scraped_links/${link.id}/add_to_schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ date: trip.start_date })
      });

      if (response.ok) {
        showNotification('success', '일정에 추가되었습니다');
        router.reload();
      }
    } catch (e) {
      showNotification('error', '일정 추가에 실패했습니다');
    }
  }

  async function deleteLink(link: any) {
    if (!confirm('이 링크를 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/scraped_links/${link.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });

      if (response.ok) {
        showNotification('success', '링크가 삭제되었습니다');
        router.reload();
      }
    } catch (e) {
      showNotification('error', '삭제에 실패했습니다');
    }
  }

  function showNotification(type: 'success' | 'error' | 'info', message: string) {
    notification = { type, message };
    setTimeout(() => notification = null, 3000);
  }

  const sourceIconPaths: Record<string, string> = {
    instagram:   '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />',
    naver_blog:  '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />',
    youtube:     '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />',
    google_maps: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />',
    tripadvisor: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />',
    other:       '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />',
  };

  function getSourceLabel(sourceType: string) {
    const labels: Record<string, string> = {
      instagram: 'Instagram',
      naver_blog: '네이버 블로그',
      youtube: 'YouTube',
      google_maps: 'Google Maps',
      tripadvisor: 'TripAdvisor',
      other: '기타'
    };
    return labels[sourceType] || labels.other;
  }

  let wishlistedLinks = $derived(scraped_links.filter(l => l.is_wishlisted));
  let otherLinks = $derived(scraped_links.filter(l => !l.is_wishlisted));
</script>

<TripLayout {trip} currentPage="wishlist">
  <div class="p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-primary">위시리스트</h1>
        <p class="text-muted mt-1">가고 싶은 장소를 저장하고 관리하세요</p>
      </div>
      <button
        onclick={openAddModal}
        class="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition"
      >
        <Plus size={20} />
        링크 추가
      </button>
    </div>

    <!-- Notification -->
    {#if notification}
      <div class="mb-4 p-4 rounded-lg {notification.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}">
        {notification.message}
      </div>
    {/if}

    {#if scraped_links.length === 0}
      <!-- Empty State -->
      <div class="text-center py-16 bg-surface rounded-xl border border-bright">
        <div class="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-pink-50">
          <svg class="w-8 h-8 text-pink-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-primary mb-2">저장된 링크가 없습니다</h3>
        <p class="text-muted mb-2">인스타그램, 블로그, 유튜브 등에서</p>
        <p class="text-muted mb-6">가고 싶은 장소 링크를 저장해보세요</p>
        <button
          onclick={openAddModal}
          class="inline-flex items-center gap-2 px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
        >
          <Plus size={20} />
          링크 추가
        </button>
      </div>
    {:else}
      <!-- Wishlisted -->
      {#if wishlistedLinks.length > 0}
        <div class="mb-8">
          <h2 class="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <Heart size={20} class="text-pink-500 fill-pink-500" />
            위시리스트 ({wishlistedLinks.length})
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {#each wishlistedLinks as link}
              <div class="bg-surface rounded-xl border border-bright overflow-hidden hover:shadow-md transition">
                {#if link.thumbnail_url}
                  <div class="h-40 bg-surface-hover">
                    <img src={link.thumbnail_url} alt="" class="w-full h-full object-cover" />
                  </div>
                {:else}
                  <div class="h-40 bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center">
                    <svg class="w-12 h-12 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {@html sourceIconPaths[link.source_type] ?? sourceIconPaths.other}
                    </svg>
                  </div>
                {/if}
                <div class="p-4">
                  <div class="flex items-start justify-between mb-2">
                    <h3 class="font-semibold text-primary line-clamp-2">{link.title || '제목 없음'}</h3>
                    <button
                      onclick={() => toggleWishlist(link)}
                      class="p-1 text-pink-500 hover:bg-pink-50 rounded"
                    >
                      <Heart size={18} class="fill-pink-500" />
                    </button>
                  </div>
                  {#if link.place_name}
                    <p class="text-sm text-secondary flex items-center gap-1 mb-2">
                      <MapPin size={14} />
                      {link.place_name}
                    </p>
                  {/if}
                  <div class="flex items-center gap-2 text-xs text-muted mb-3">
                    <span class="px-2 py-0.5 bg-surface-hover rounded">{getSourceLabel(link.source_type)}</span>
                  </div>
                  <div class="flex gap-2">
                    <a
                      href={link.original_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-surface-hover text-secondary rounded-lg hover:bg-surface-hover"
                    >
                      <ExternalLink size={14} />
                      원본 보기
                    </a>
                    <button
                      onclick={() => addToSchedule(link)}
                      class="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Calendar size={14} />
                      일정 추가
                    </button>
                  </div>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Other Links -->
      {#if otherLinks.length > 0}
        <div>
          <h2 class="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <LinkIcon size={20} class="text-muted" />
            저장된 링크 ({otherLinks.length})
          </h2>
          <div class="space-y-3">
            {#each otherLinks as link}
              <div class="bg-surface rounded-xl border border-bright p-4 hover:shadow-md transition">
                <div class="flex items-center gap-4">
                  <div class="w-12 h-12 bg-surface-hover rounded-lg flex items-center justify-center">
                    <svg class="w-6 h-6 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {@html sourceIconPaths[link.source_type] ?? sourceIconPaths.other}
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="font-medium text-primary truncate">{link.title || link.original_url}</h3>
                    <div class="flex items-center gap-2 text-sm text-muted">
                      <span>{getSourceLabel(link.source_type)}</span>
                      {#if link.place_name}
                        <span>•</span>
                        <span class="flex items-center gap-1">
                          <MapPin size={12} />
                          {link.place_name}
                        </span>
                      {/if}
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      onclick={() => toggleWishlist(link)}
                      class="p-2 text-muted hover:text-pink-500 hover:bg-pink-50 rounded-lg"
                    >
                      <Heart size={18} />
                    </button>
                    <a
                      href={link.original_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="p-2 text-muted hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <ExternalLink size={18} />
                    </a>
                    <button
                      onclick={() => deleteLink(link)}
                      class="p-2 text-muted hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/if}
  </div>

  <!-- Add Link Modal -->
  {#if showModal}
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-surface rounded-2xl w-full max-w-md">
        <div class="p-6 border-b border-bright">
          <h2 class="text-xl font-bold">링크 추가</h2>
          <p class="text-sm text-muted mt-1">인스타그램, 블로그, 유튜브 등의 URL을 붙여넣으세요</p>
        </div>

        <form onsubmit={(e) => { e.preventDefault(); addLink(); }} class="p-6">
          <div class="mb-4">
            <input
              type="url"
              bind:value={urlInput}
              placeholder="https://..."
              class="w-full px-4 py-3 border border-bright rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            />
          </div>

          <div class="bg-surface-hover rounded-lg p-4 mb-4">
            <p class="text-sm text-secondary font-medium mb-2">지원하는 플랫폼</p>
            <div class="flex flex-wrap gap-2">
              <span class="px-2 py-1 bg-surface rounded text-xs flex items-center gap-1"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg> Instagram</span>
              <span class="px-2 py-1 bg-surface rounded text-xs flex items-center gap-1"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg> 네이버 블로그</span>
              <span class="px-2 py-1 bg-surface rounded text-xs flex items-center gap-1"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> YouTube</span>
              <span class="px-2 py-1 bg-surface rounded text-xs flex items-center gap-1"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg> Google Maps</span>
              <span class="px-2 py-1 bg-surface rounded text-xs flex items-center gap-1"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg> TripAdvisor</span>
            </div>
          </div>

          <div class="flex justify-end gap-3">
            <button
              type="button"
              onclick={closeModal}
              class="px-4 py-2 text-secondary bg-surface-hover rounded-lg hover:bg-surface-hover"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isLoading}
              class="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50"
            >
              {isLoading ? '추가 중...' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  {/if}
</TripLayout>
